import React, { useState } from "react";
import { DraftReviewDialogProps } from "./types";
import { DraftStatusBadge } from "./DraftStatusBadge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  X,
  CheckCircle,
  XCircle,
  MessageSquare,
  Clock,
  User,
  AlertCircle,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export const DraftReviewDialog: React.FC<DraftReviewDialogProps> = ({
  draft,
  open,
  onOpenChange,
  onSubmitReview,
}) => {
  const [reviewAction, setReviewAction] = useState<"approve" | "reject" | null>(
    null,
  );
  const [comments, setComments] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (action: "approve" | "reject") => {
    setIsSubmitting(true);

    try {
      await onSubmitReview(draft.id, action, comments.trim() || undefined);

      // Reset form
      setReviewAction(null);
      setComments("");
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onOpenChange(false);
      setReviewAction(null);
      setComments("");
    }
  };

  const getUserInitials = (name: string): string => {
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const userInitials = getUserInitials(draft.createdBy);
  const timeAgo = formatDistanceToNow(draft.updatedAt, { addSuffix: true });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center space-x-2">
              <MessageSquare className="w-5 h-5" />
              <span>Review Draft</span>
            </DialogTitle>

            <DialogClose asChild>
              <Button variant="outline" size="sm" disabled={isSubmitting}>
                <X className="w-4 h-4" />
              </Button>
            </DialogClose>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Draft Summary */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-start space-x-4">
                <Avatar className="w-10 h-10">
                  <AvatarFallback>{userInitials}</AvatarFallback>
                </Avatar>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="font-semibold text-base">{draft.name}</h3>
                    <DraftStatusBadge status={draft.status} />
                  </div>

                  {draft.description && (
                    <p className="text-sm text-muted-foreground mb-3">
                      {draft.description}
                    </p>
                  )}

                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <div className="flex items-center space-x-1">
                      <User className="w-3 h-3" />
                      <span>By {draft.createdBy}</span>
                    </div>

                    <div className="flex items-center space-x-1">
                      <Clock className="w-3 h-3" />
                      <span>Updated {timeAgo}</span>
                    </div>

                    <div className="flex items-center space-x-1">
                      <span>{draft.changes?.length || 0} changes</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Review Comments */}
          <div>
            <Label htmlFor="review-comments">
              Review Comments
              <span className="text-muted-foreground font-normal ml-1">
                (optional)
              </span>
            </Label>
            <Textarea
              id="review-comments"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder={
                reviewAction === "approve"
                  ? "Add any feedback or notes about the approved changes..."
                  : reviewAction === "reject"
                    ? "Please explain what needs to be changed before approval..."
                    : "Add comments about this draft..."
              }
              disabled={isSubmitting}
              rows={4}
              className="mt-2"
            />
          </div>

          {/* Review Actions */}
          <div className="space-y-4">
            <h4 className="font-medium text-sm">Review Decision</h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Approve */}
              <Card
                className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                  reviewAction === "approve"
                    ? "ring-2 ring-green-500 bg-green-50 dark:bg-green-950"
                    : ""
                }`}
                onClick={() => setReviewAction("approve")}
              >
                <CardContent className="p-4 text-center">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                  <h4 className="font-medium text-sm mb-1">Approve Draft</h4>
                  <p className="text-xs text-muted-foreground">
                    Mark this draft as approved and ready for publishing
                  </p>
                </CardContent>
              </Card>

              {/* Reject */}
              <Card
                className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                  reviewAction === "reject"
                    ? "ring-2 ring-red-500 bg-red-50 dark:bg-red-950"
                    : ""
                }`}
                onClick={() => setReviewAction("reject")}
              >
                <CardContent className="p-4 text-center">
                  <XCircle className="w-8 h-8 mx-auto mb-2 text-red-500" />
                  <h4 className="font-medium text-sm mb-1">Reject Draft</h4>
                  <p className="text-xs text-muted-foreground">
                    Request changes before this draft can be approved
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Warning for rejection without comments */}
            {reviewAction === "reject" && !comments.trim() && (
              <div className="flex items-start space-x-2 p-3 bg-orange-50 dark:bg-orange-950 border border-orange-200 dark:border-orange-800 rounded-lg">
                <AlertCircle className="w-4 h-4 text-orange-500 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-orange-800 dark:text-orange-200">
                    Consider adding feedback
                  </p>
                  <p className="text-orange-600 dark:text-orange-300">
                    Help the author understand what needs to be changed by
                    providing specific feedback.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Submit Actions */}
          <div className="flex items-center justify-end space-x-2 pt-4">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>

            {reviewAction && (
              <Button
                onClick={() => handleSubmit(reviewAction)}
                disabled={isSubmitting}
                className={
                  reviewAction === "approve"
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-red-600 hover:bg-red-700"
                }
              >
                {isSubmitting ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Submitting...</span>
                  </div>
                ) : (
                  <>
                    {reviewAction === "approve" ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approve Draft
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject Draft
                      </>
                    )}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DraftReviewDialog;
